import { useState } from 'react';
import { Download } from 'lucide-react';
import Swal from 'sweetalert2';

interface Props {
  /** Funzione che effettua la chiamata di export e ritorna il blob XLSX. */
  fetchBlob: () => Promise<Blob>;
  /** Nome del file scaricato, SENZA estensione (viene aggiunto `.xlsx`). */
  fileName:  string;
  /** Tema del bottone: `amber` per i driver di Investigation, `navy` altrove. */
  variant?:  'navy' | 'amber';
  /** Etichetta del bottone (default: "Scarica XLSX"). */
  label?:    string;
}

/**
 * Bottone di export XLSX riutilizzabile per i driver Conduct Risk e per la
 * tabella di Investigation Summary. Incapsula fetch del blob, creazione del
 * link di download e gestione errori (Swal), cosi' da evitare duplicazioni.
 */
export const ExportXlsxButton = ({ fetchBlob, fileName, variant = 'amber', label = 'Scarica XLSX' }: Props) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Export fallito', text: err?.message ?? 'Impossibile scaricare il file XLSX.' });
    } finally {
      setIsExporting(false);
    }
  };

  const tone = variant === 'navy'
    ? 'text-[#1B365D] border-[#1B365D]/20 hover:bg-[#1B365D]/5'
    : 'text-amber-800 border-amber-300 hover:bg-amber-100';

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0 ${tone}`}
    >
      <Download className="w-3.5 h-3.5" />
      {isExporting ? 'Esportazione…' : label}
    </button>
  );
};

export default ExportXlsxButton;
